import * as Reach from '@reach/menu-button';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import styled from 'styled-components';

import { FUTURES_ROUTE } from '#/routes';

import { c1D1D37 } from '#/colors';

import {
  MarketsMenuProvider,
  useMarketsMenuView,
} from '#/features/markets-menu/markets-menu-context';

import { Empty, Error } from '#/components/shared/trade-overview/atoms';
import { Loadable } from '#/components/shared/Loadable';
import { MEDIA } from '#/components/shared/media-queries';
import AssetIcon from '#/components/shared/AssetIcon';

import { TableSpec, createTable, StyledTable } from './table';
import { POPOVER_PADDING } from './styles';

type Columns = 'market' | 'lastPrice' | 'openInterest' | 'volume24h';

const tableSpec: TableSpec<Columns> = {
  market: { header: 'Market', width: '140px' },
  lastPrice: { header: 'Last Price' },
  openInterest: { header: 'Open Interest' },
  volume24h: { header: '24h Volume' },
};

export default function MarketsTable() {
  return (
    <MarketsMenuProvider>
      <MarketsTableContent />
    </MarketsMenuProvider>
  );
}

function MarketsTableContent() {
  const navigate = useNavigate();
  const [Table, Row] = useMemo(() => createTable(tableSpec), []);
  const marketsMenuView = useMarketsMenuView();

  if (marketsMenuView.status === 'error') {
    return <Error>{marketsMenuView.error}</Error>;
  }

  const isEmpty = marketsMenuView.futureMarkets.length === 0;
  const isLoading =
    marketsMenuView.status === 'loading' ||
    marketsMenuView.status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>No markets available</Empty>
      </Loadable>
    );
  }

  return (
    <Container>
      <Loadable isLoading={isLoading}>
        <Table>
          {marketsMenuView.futureMarkets.map((market) => (
            <Reach.MenuLink
              as={Row}
              onClick={() => navigate(`${FUTURES_ROUTE}/${market.symbol}`)}
              children={null} // eslint-disable-line react/no-children-prop
              key={market.symbol}
              market={<Market market={market} />}
              lastPrice={market.lastTradedPrice}
              openInterest={market.openInterest}
              volume24h={market.volume24h}
            />
          ))}
        </Table>
      </Loadable>
    </Container>
  );
}

function Market(props: { market: { symbol: string } }) {
  const { market } = props;

  return (
    <div css="display: flex; align-items: center; gap: 8px;">
      <AssetIcon asset={market.symbol} size={14} /> {market.symbol}
    </div>
  );
}

const Container = styled.div`
  ${StyledTable} {
    /* tables do not support 'box-sizing: border-box' */
    width: calc(100% - ${POPOVER_PADDING}px * 2);

    tbody > tr {
      &:hover {
        cursor: pointer;
        background-color: lightyellow;
        background-color: ${c1D1D37};
      }

      th:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      td:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }

    td {
      padding-left: 8px;
    }

    th:not(:first-child) {
      padding-left: 8px;
    }

    @media ${MEDIA.md} {
      th:first-child {
        padding-left: 8px;
      }
    }
  }
`;
